import {createTheme, defaultCssVariablesResolver, type MantineTheme} from '@mantine/core'

export const cssVariablesResolver = (mantineTheme: MantineTheme) => {
    const resolved = defaultCssVariablesResolver(mantineTheme)
    return {
        ...resolved,
        light: {
            ...resolved.light,
            '--mantine-color-dimmed': 'var(--mantine-color-gray-7)',
        },
    }
}

/** Magenta #750075 — shared by `purple` (gaps / negative UI) and `disengaged` (engagement badges). */
const MAGENTA_SCALE = [
    '#F6E6F6',
    '#EDD3ED',
    '#E4C0E4',
    '#D5A0D5',
    '#C680C6',
    '#AE60AE',
    '#750075',
    '#630063',
    '#510051',
    '#3F003F',
] as const

/** Navy #1E1E46, green #70AD47, purple #750075, orange #EE964B — Rose City style guide. */
export const theme = createTheme({
    primaryColor: 'brand',
    fontFamily: '"Source Sans 3", sans-serif',
    fontSizes: {
        xs: '0.875rem',
        sm: '1rem',
        md: '1.125rem',
        lg: '1.25rem',
        xl: '1.5rem',
    },
    headings: {
        fontFamily: '"Montserrat", sans-serif',
    },
    defaultRadius: 'md',
    colors: {
        brand: [
            '#F4F4F8',
            '#E8E8EE',
            '#D6D6DF',
            '#C0C0CE',
            '#AAAABD',
            '#8888A4',
            '#65658B',
            '#4A4A72',
            '#333358',
            '#1E1E46',
        ],
        /** Gaps, not-met, and other negative / incomplete UI (same hexes as disengaged). */
        purple: [...MAGENTA_SCALE],
        /** Disengaged engagement — same scale as `purple`; separate key so primary UI can go green. */
        disengaged: [...MAGENTA_SCALE],
        /** Very Engaged — #70AD47 */
        green: [
            '#F4FAF0',
            '#E8F5E0',
            '#D1EBC0',
            '#BAE1A0',
            '#A3D780',
            '#8CCD64',
            '#70AD47',
            '#5E943C',
            '#4C7B31',
            '#3A6226',
        ],
        /** Engaged — #AFA249 */
        engaged: [
            '#FAF8F0',
            '#F5F2E0',
            '#EBE4C0',
            '#E1D6A0',
            '#D7C880',
            '#CDBA68',
            '#AFA249',
            '#958940',
            '#7B7034',
            '#615728',
        ],
        /** Somewhat Engaged — #EE964B */
        orange: [
            '#FDF6EF',
            '#FBECDF',
            '#F7D8BF',
            '#F3C49F',
            '#EFB07F',
            '#EB9C67',
            '#EE964B',
            '#CA7F3F',
            '#A66833',
            '#825127',
        ],
        /** Minimally Engaged — #B24B60 */
        rose: [
            '#F8ECEE',
            '#F0D5DB',
            '#E4B3BE',
            '#D891A1',
            '#CC6F84',
            '#BF5D6F',
            '#B24B60',
            '#96404F',
            '#7A353E',
            '#5E2A2D',
        ],
    },
    components: {
        Card: {
            defaultProps: {
                withBorder: false,
            },
            styles: {
                root: {
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                },
            },
        },
    },
})
